import { RouterEventDetail } from '@ionic/core';
import { Fragment, h } from '@stencil/core';
import {
  getToday,
  getTodayString,
  formatDate,
  INCOME_BUDGET_ID,
} from '@og-shared/utils';
import { addDays } from 'date-fns';

import { uiState } from '../../store/ui.store';
import { signOut } from '../../store/actions/auth';
import { routeInApp } from '../../global/route-in-app';
import { waitFor } from '../../global/utils';
import { partnerState } from '../../store/partner.store';

const routeWillChangeHandler = (event: CustomEvent<RouterEventDetail>) => {
  const url = event.detail.to;
  uiState.active_route = url;
};

const routeDidChangeHandler = async (event: CustomEvent<RouterEventDetail>) => {
  const url = event.detail.to;
  if (url !== '/sign-out') return;
  await waitFor(3000);
  await signOut();
  await routeInApp('/', 'root');
};

export function renderRouter(
  auth: AuthState,
  featureFlags: UiState['features']
) {
  return (
    <ion-router
      useHash={false}
      onIonRouteWillChange={routeWillChangeHandler}
      onIonRouteDidChange={routeDidChangeHandler}
    >
      <ion-route
        url="/referred/:invited_by_uid"
        component="og-create-group-page"
      />
      <ion-route url="/sign-out" component="og-loading-page" />
      <ion-route url="/exit-app" component="og-loading-page" />
      <ion-route url="/groups" component="og-create-group-page" />
      <ion-route url="/groups/:groupId" component="og-in-app">
        {/* START SPLIT PANE - groupId in URL */}
        <ion-route url="/" component="og-group-loading-page" />
        <ion-route url="/intro" component="og-intro-page" />
        <ion-route url="/intro/goal" component="og-intro-goal-page" />
        <ion-route
          url="/intro/link-items"
          component="og-intro-link-items-page"
        />
        <ion-route
          url="/intro/link-accounts"
          component="og-intro-link-accounts-page"
        />
        <ion-route
          url="/intro/analyze-spending"
          component="og-intro-analyze-spending-page"
        />
        <ion-route
          url="/intro/categorize-spending"
          component="og-categorize-spending-page"
          componentProps={{
            defaultHref: '/intro/link-accounts',
          }}
        />
        <ion-route
          url="/intro/review-budget"
          component="og-intro-review-budget-page"
        />
        <ion-route url="/intro/one-goal" component="og-intro-one-goal-page" />
        <ion-route
          url="/intro/suggested-categories/:type"
          component="og-intro-suggested-categories-page"
        />
        <ion-route url="/intro/income" component="og-intro-income-page" />
        <ion-route
          url="/intro/suggested-bills"
          component="og-intro-suggested-bills-page"
        />
        <ion-route url="/intro/bills" component="og-intro-bills-page" />
        <ion-route
          url="/intro/suggested-savings"
          component="og-intro-suggested-savings-page"
        />
        <ion-route url="/intro/savings" component="og-intro-savings-page" />
        <ion-route
          url="/intro/suggested-spending"
          component="og-intro-suggested-spending-page"
        />
        <ion-route url="/intro/spending" component="og-intro-spending-page" />
        <ion-route
          url="/budgets"
          component={
            featureFlags.spending_page_all
              ? 'og-budgets-all-page'
              : 'og-budgets-tabs'
          }
        />
        <ion-route url="/budgets-all" component="og-budgets-all-page" />
        <ion-route url="/help" component="og-help-page" />
        <ion-route url="/help/reset" component="og-reset-page" />
        <ion-route url="/settings" component="og-settings-page" />
        <ion-route url="/settings/features" component="og-features-page" />
        <ion-route
          url="/settings/manage-access"
          component="og-manage-access-page"
        />
        <ion-route
          url="/settings/notifications"
          component="og-notifications-page"
        />
        <ion-route url="/settings/rules" component="og-rules-list" />
        <ion-route
          url="settings/rules/test"
          component="og-test-all-rules-page"
        />
        <ion-route url="/release-notes" component="og-release-notes-page" />
        <ion-route url="/budgets/:budgetId" component="og-budget-page" />
        <ion-route
          url="/budgets/:budget_id/projections"
          component="og-budget-projections-page"
        />
        <ion-route
          url="/projections-table"
          component="og-projections-table-page"
        />
        <ion-route url="/plan" component="og-plan-page" />
        <ion-route url="/dashboard" component="og-dashboard-page" />
        <ion-route url="/reports" component="og-reports-page" />
        <ion-route url="/custom-report" component="og-custom-reports-page" />
        <ion-route url="/reports/:budgetId" component="og-budget-report-page" />
        <ion-route
          // must be in this order to allow nav.push component
          url="/intro/review-budget/:budgetId"
          component="og-budget-report-page"
          componentProps={{
            startDate: formatDate(addDays(getToday(), -365)),
            endDate: getTodayString(),
            dateSegment: 'custom',
          }}
        />
        <ion-route
          url="/reports-table"
          component="og-reports-page"
          componentProps={{ showTable: true }}
        />
        <ion-route
          url="/reports/:budgetId/categorize"
          component="og-categorize-spending-page"
          componentProps={{
            defaultHref: '/reports/IGNORE',
          }}
        />
        <ion-route
          url="/accounts"
          component="og-accounts-page"
          componentProps={{ activeSegment: 'SPENDING' }}
        />
        <ion-route
          url="/net-worth"
          component="og-accounts-page"
          componentProps={{ activeSegment: 'NET_WORTH' }}
        />
        <ion-route
          url="/accounts/cash-flow"
          component="og-accounts-cash-flow-page"
        />
        <ion-route url="/accounts/:accountId" component="og-account-page" />
        <ion-route
          url="/accounts/:account_id/projections"
          component="og-account-projections-page"
        />
        <ion-route url="/investments" component="og-investments-page" />
        <ion-route
          url="/subscribe/success"
          component="og-subscribe-status-page"
        />
        <ion-route
          url="/subscribe/cancel"
          component="og-subscribe-cancel-page"
        />
        <ion-route url="/promo/:promoId" component="og-subscribe-modal" />
        <ion-route url="/connections" component="og-connections-page" />
        <ion-route url="/connections/:itemId" component="og-item-page" />
        <ion-route
          url="/connections/:itemId/plaid-webhooks"
          component="og-plaid-webhooks-page"
        />
        <ion-route
          url="/connections/:itemId/webhooks"
          component="og-webhooks-page"
        />
        <ion-route url="/transactions" component="og-transactions-page" />
        <ion-route
          url="/transactions/search"
          component="og-search-transactions-page"
        />
        <ion-route
          url="/income"
          component="og-income-page"
          componentProps={{ budget_id: INCOME_BUDGET_ID }}
        />
        <ion-route url="/billing" component="og-billing-page" />
        <ion-route url="/share" component="og-share-page" />
        <ion-route url="/share/access-codes" component="og-access-codes-page" />
        <ion-route url="/invites" component="og-invites-page" />
        <ion-route url="/nerd-view" component="og-nerd-view" />
        {/* END SPLIT PANE - groupId in URL */}
      </ion-route>
      <ion-route url="/portal/:partnerId" component="og-portal">
        <ion-route
          url="/"
          component="og-partner-details-page"
          componentProps={{ partnerId: partnerState.partner?.partner_id }}
        />
        <ion-route
          url="/shared-data"
          component="og-shared-data-page"
          componentProps={{ partnerId: partnerState.partner?.partner_id }}
        />
        <ion-route
          url="/shared-data/:groupId"
          component="og-shared-data-detail-page"
          componentProps={{ partnerId: partnerState.partner?.partner_id }}
        />
        <ion-route
          url="/authorized-users"
          component="og-authorized-users-page"
          componentProps={{ partnerId: partnerState.partner?.partner_id }}
        />
        <ion-route
          url="/authorized-users-changes"
          component="og-authorized-users-changes-page"
          componentProps={{ partnerId: partnerState.partner?.partner_id }}
        />
        <ion-route
          url="/authorized-users-changes/:authorizedUserId"
          component="og-authorized-users-changes-page"
          componentProps={{ partnerId: partnerState.partner?.partner_id }}
        />
      </ion-route>
      {uiState.local && (
        <Fragment>
          <ion-route
            url="/analyze-spending"
            component="og-admin-analyze-spending-debug-page"
          />
          <ion-route url="/debug-rules" component="og-admin-rules-debug-page" />
        </Fragment>
      )}
      {auth.userInfo.admin ? (
        <ion-route url="/admin" component="ion-nav">
          <ion-route url="/" component="og-admin-page" />
          <ion-route
            url="/analyze-spending/:groupId"
            component="og-admin-analyze-spending-page"
          />
          <ion-route url="/promos" component="og-promos-page" />
          <ion-route
            url="/access-codes"
            component="og-admin-access-codes-page"
          />
          <ion-route url="/groups" component="og-admin-groups-page" />
          <ion-route url="/groups/:groupId" component="og-admin-groups-page" />
          <ion-route url="/docs" component="og-admin-docs-page" />
          <ion-route
            url="/referrals/:fieldValue"
            component="og-admin-groups-page"
            componentProps={{
              fieldPath: 'invited_by_uid',
              whereFilterOp: '==',
            }}
          />
          <ion-route url="/users" component="og-admin-users-page" />
          <ion-route url="/view-feedback" component="og-view-feedback" />
          <ion-route url="/partners" component="og-partners-page" />
          <ion-route url="/partners/:partnerId" component="og-partner-page" />
          <ion-route
            url="/partners/:partnerId/billing"
            component="og-authorized-users-billing-page"
          />
          <ion-route
            url="/partners/:partnerId/suggested-categories"
            component="og-partner-suggested-categories-page"
          />
          <ion-route
            url="/pending-invites"
            component="og-admin-pending-invites-page"
          />
          <ion-route url="/edit-budget" component="og-admin-edit-budget-page" />
          <ion-route url="/update-data" component="og-admin-update-data-page" />
          <ion-route url="/conversations" component="og-conversations-page" />
          <ion-route
            url="/conversations/:conversation_id"
            component="og-conversation-page"
            componentProps={{ isAdmin: true }}
          />
        </ion-route>
      ) : null}
      {/* START NO LOGIN REQUIRED */}
      <ion-route url="/settings/plaid-link" component="og-plaid-link-page" />
      <ion-route
        url="/settings/plaid-link-oauth"
        component="og-plaid-link-page"
        componentProps={{ oAuth: true }}
      />
      <ion-route url="/import-budget" component="ion-nav">
        <ion-route url="/" component="og-import-budget-transactions" />
        <ion-route
          url="/review-spending"
          component="og-import-budget-review-spending"
        />
        <ion-route
          url="/review-budget"
          component="og-import-budget-review-budget"
        />
      </ion-route>
      <ion-route url="/" component="og-landing-page" />
      <ion-route url="/sign-in" component="og-landing-page" />
      <ion-route
        url="/sign-in-email"
        component="og-landing-page"
        componentProps={{ providerOptions: 'email-only' }}
      />
      <ion-route url="/join" component="og-join-page" />
      <ion-route url="/join/:invite_id" component="og-join-page" />
      <ion-route
        url="/partners/:partnerId/:authorizedUserId"
        component="og-join-partner-page"
      />
      <ion-route url="/design/style-guide" component="og-style-guide-page" />
      <ion-route url="/design/budget-bar" component="og-budget-bar-page" />
      {/* END NO LOGIN REQUIRED */}
      {handleRedirects(auth)}
      {defaultRedirects()}
      {auth.userInfo.authLoaded && (
        <ion-route url=":any" component="og-404-page" />
      )}
    </ion-router>
  );
}

const handleRedirects = (auth: AuthState) => {
  const authLoaded = auth.userInfo.authLoaded;
  const isAuthenticated = auth.userInfo.authenticated;
  const stored_group_id = auth.stored_group_id;
  if (authLoaded) {
    if (isAuthenticated) {
      // auth loaded & authenticated
      // console.log('auth loaded and authenticated', {
      //   auth: { ...auth },
      // });

      if (auth.userInfo.claimsSet && !auth.userInfo.joinedGroup) {
        // hasn't joined a group
        // console.log("hasn't joined group");
        return kickOut('/groups');
      }
      // claims set
      return sendInApp(stored_group_id);
    } else {
      // auth loaded not authenticated
      return kickOut('/sign-in');
    }
  } else {
    if (stored_group_id) {
      return sendInApp(stored_group_id);
    } else {
      return [];
    }
  }
};

function defaultRedirects() {
  return [
    <ion-route-redirect from="/login" to="/sign-in" />,
    <ion-route-redirect from="/" to="/sign-in" />,
    <ion-route-redirect from="/create-group" to="/groups" />,
  ];
}

function sendInApp(storedGroupId: AuthState['stored_group_id']) {
  if (!storedGroupId) {
    // group not loaded yet
    return [];
  }
  const home = `/groups/${storedGroupId}`;
  // console.log('redirect into app', home);
  return [
    <ion-route-redirect from="/" to={home} />,
    <ion-route-redirect from="/sign-in" to={home} />,
    <ion-route-redirect from="/sign-in-email" to={home} />,
  ];
}

function kickOut(toURL: '/sign-in' | '/groups') {
  // console.log('kick out', toURL);
  return [
    <ion-route-redirect from="/groups/*" to={toURL} />,
    // don't send sign-in kick out away from landing page
    toURL === '/sign-in' ? null : (
      <Fragment>
        <ion-route-redirect from="/sign-in" to={toURL} />
        <ion-route-redirect from="/sign-in-email" to={toURL} />
      </Fragment>
    ),
  ];
}
